import CustomAutoComplete, {
  Option,
} from "@/modules/common/components/input/dropdowns/auto-complete";
import { InterestProperty } from "@/modules/lead-page/types";
import { useMemo, useState } from "react";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Box, Typography } from "@mui/material";
import Textarea from "@/modules/common/components/input/text-area";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { addInterestActionSchema } from "../../../utils/interests";
import z from "zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useAddInterestAction from "../../../hooks/interests/add-interest-action.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { useEligibleInterestActionTypesFilterOptions } from "../../../hooks/interests/eligible-interest-action-types.hook";

type AddInterestActionFormFields = z.infer<
  ReturnType<typeof addInterestActionSchema>
>;

export default function InterestActionDropdown({
  interest,
}: {
  interest: InterestProperty;
}) {
  const [open, setOpen] = useState(false);
  const value = {
    label: interest.lastAction?.interestActionType.name,
    value: interest.lastAction?.interestActionType.id,
    color: interest.lastAction?.interestActionType.color,
  };

  const { handleSubmit, reset, control, setValue, watch } =
    useForm<AddInterestActionFormFields>({
      resolver: zodResolver(addInterestActionSchema()),
      defaultValues: {
        interestActionType: value ?? undefined,
      },
    });

  const selectedValue = useMemo(
    () => watch("interestActionType"),
    [watch("interestActionType")]
  );

  const mutation = useAddInterestAction();

  const onSubmit: SubmitHandler<AddInterestActionFormFields> = async (data) => {
    submitAddInterestAction(data);
  };

  const submitAddInterestAction = (data: AddInterestActionFormFields) => {
    mutation.mutate(
      {
        data: {
          interestActionTypeId: data.interestActionType.value,
          interestUid: interest.uid,
          comment: data.comment,
        },
      },
      {
        onSuccess: () => {
          handleCloseModal();
          showToast({
            type: ToastType.SUCCESS,
            message: `Action added successfully.`,
            toastId: TOAST_IDS.ADD_INTEREST_ACTION_SUCCESS,
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_INTEREST_ACTION_ERROR,
          });
        },
      }
    );
  };

  const handleCloseModal = () => {
    setOpen(false);
    reset();
  };
  const { data: interestActionTypes, isLoading: interestActionTypesLoading } =
    useEligibleInterestActionTypesFilterOptions({
      interestUid: interest.uid,
    });

  const handleChangeInterestAction = (option: Option | Option[] | null) => {
    const selected = option as Option;
    setValue("interestActionType", {
      label: selected.label,
      value: Number(selected.value),
    });
    setOpen(true);
  };

  return (
    <>
      <Controller
        control={control}
        name="interestActionType"
        render={({ field }) => (
          <CustomAutoComplete
            {...field}
            showTooltip={false}
            options={
              interestActionTypes?.map((i) => ({
                value: i.id.toString(),
                label: i.name,
                color: i.color,
              })) ?? []
            }
            disabled={interest.lastAction?.interestActionType.closingAction}
            placeholder="Interest Action"
            value={
              value &&
              ({
                label: value.label,
                value: value.value?.toString(),
                color: value.color,
              } as Option)
            }
            onChange={handleChangeInterestAction}
            fullWidth
            disableClearable={true}
            loading={interestActionTypesLoading}
          />
        )}
      />
      <EditFieldModal
        open={open}
        setOpen={setOpen}
        header={"Add Interest Action"}
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutation.isPending}
      >
        <Box sx={(t) => ({ display: "flex", gap: t.nawy.spacing.xs })}>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Interest action type:
          </Typography>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.regular}
          >
            {selectedValue.label}
          </Typography>
        </Box>
        <Controller
          control={control}
          name="comment"
          render={({ field, fieldState }) => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <CustomLabel
                  sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
                >
                  Comment
                </CustomLabel>
              </Box>
              <Textarea
                placeholder="comment"
                {...field}
                isError={!!fieldState.error}
                errorMsg={fieldState.error?.message}
              />
            </Box>
          )}
        />
      </EditFieldModal>
    </>
  );
}
